import { IImageWithObjectKey, IImageWithUrl, IPaginatedContentResponse, IPaginationParams, SortOrder } from './shared.dto';
import { JobDtoType } from '../domain/jobs';
import { IEventAttributeValue } from './events.dao';
import { IAccountContactDTO } from './account-contacts.dao';

export abstract class IEventHubsDAO {
    public abstract getEventHubs(accountId: number, paginationParams?: IPaginationParams): Promise<IEventHubListDTO>;
    public abstract createEventHub(accountId: number, params: IEventHubRequestDTO): Promise<IEventHubDTO>;
    public abstract updateEventHub(accountId: number, eventHubId: number, params: IEventHubRequestDTO): Promise<IEventHubDTO>;
    public abstract getEventHub(accountId: number, eventHubId: number): Promise<IEventHubDTO>;
    public abstract deleteEventHub(accountId: number, eventHubId: number): Promise<void>;
    public abstract updateEventHubSubdomain(accountId: number, eventHubId: number, payload: IUpdateEventHubSubdomainDTO): Promise<IEventHubDTO>;
    public abstract createHubOrganization(accountId: number, eventHubId: number, params: ICreateHubOrganizationDTO): Promise<IHubOrganizationDTO>;
    public abstract updateHubOrganization(
        accountId: number,
        eventHubId: number,
        organizationId: number,
        params: ICreateHubOrganizationDTO
    ): Promise<IHubOrganizationDTO>;
    public abstract getHubOrganization(accountId: number, eventHubId: number, organizationId: number): Promise<IHubOrganizationDTO>;
    public abstract deleteHubOrganization(accountId: number, eventHubId: number, organizationId: number): Promise<JobDtoType | void>;
    public abstract getHubOrganizations(accountId: number, eventHubId: number): Promise<IHubOrganizationDTO[]>;
    public abstract getHubAttendees(params: IGetHubAttendeesRequestParams): Promise<IHubAttendeesListDTO>;
    public abstract createHubAttendee(accountId: number, eventHubId: number, params: ICreateHubAttendeeDTO): Promise<IHubAttendeeDTO>;
    public abstract updateHubAttendee(accountId: number, eventHubId: number, attendeeId: number, params: ICreateHubAttendeeDTO): Promise<IHubAttendeeDTO>;
    public abstract deleteHubAttendee(accountId: number, eventHubId: number, attendeeId: number): Promise<void>;
    public abstract getEventHubConfigs(accountId: number, eventHubId: number): Promise<IHubConfigurationSettingsDTO>;
    public abstract setEventHubConfig(params: THubConfigurationDTO | null): Promise<THubConfigurationDTO>;
    public abstract getEventHubVersions(eventHubId: number): Promise<IEventHubVersionsDTO>;
    public abstract getHubPages(hubVersionId: number): Promise<IHubPageDTO[]>;
    public abstract getMenuItems(hubVersionId: number): Promise<(IHubPageDTO | IHubMenuLinkDTO)[]>;
    public abstract getHubPage(hubPageId: number): Promise<IHubPageDetailsDTO>;
    public abstract createHubPage(hubVersionId: number, params: ICreateHubPageDTO): Promise<IHubPageDetailsDTO>;
    public abstract updateHubPage(params: IUpdateHubPageDTO): Promise<IHubPageDetailsDTO>;
    public abstract createHubMenuLink(hubVersionId: number, params: ICreateHubMenuLinkDTO): Promise<IHubMenuLinkDTO>;
    public abstract updateHubMenuLink(hubMenuLinkId: number, params: ICreateHubMenuLinkDTO): Promise<IHubMenuLinkDTO>;
    public abstract reorderMenuItems(hubVersionId: number, orderedMenuItemIds: number[]): Promise<(IHubPageDTO | IHubMenuLinkDTO)[]>;
}

// ── Event Hub ─────────────────────────────────────────────────────────────────

export interface IEventHubDTO {
    eventHubId: number;
    eventHubName: string;
    createdAt: string;
    subdomain: string | null;
    registrationApprovalEnabled?: boolean;
}

export interface IEventHubListDTO extends IPaginatedContentResponse<IEventHubDTO> {}

export interface IEventHubRequestDTO extends Omit<IEventHubDTO, 'eventHubId' | 'createdAt' | 'subdomain' | 'registrationApprovalEnabled'> {}

export interface IUpdateEventHubSubdomainDTO {
    subdomain: string | null;
}

export interface IEventHubVersionsDTO {
    eventHubId: number;
    previewVersionId: number;
}

// ── Hub Configuration Settings ───────────────────────────────────────────────

export enum HubConfigurationSettingsType {
    REGISTRATION = 'HUB_REGISTRATION',
    SIGN_IN = 'HUB_SIGN_IN',
    BRANDING = 'HUB_BRANDING',
    FOOTER = 'HUB_FOOTER',
}

export interface ICommonHubSettingsDTO {
    accountId: number;
    eventHubId: number;
    type: HubConfigurationSettingsType;
}

export interface IHubRegistrationDTO extends ICommonHubSettingsDTO {
    registrationEnabled: boolean;
    registrationApprovalEnabled: boolean;
    registrationDisplayName: string | null;
    registrationInstructions: string | null;
    registerButtonLabel: string;
    registrationSubmittedMessage: string;
    oneStepRegistrationEnabled: boolean;
}

export interface IHubSignInDTO extends ICommonHubSettingsDTO {
    signInDisplayName: string | null;
    requestLinkInstructions: string | null;
    requestLinkButtonLabel: string;
    signInLinkRequestedMessage: string;
    oneStepSignInEnabled: boolean;
}

export interface IHubBrandingDTO extends ICommonHubSettingsDTO {
    primaryColor: string | null;
    logoImage?: IImageWithObjectKey;
    logoImageWithUrl?: IImageWithUrl;
    backgroundImage?: IImageWithObjectKey;
    backgroundImageWithUrl?: IImageWithUrl;
    customCss?: string | null;
}

export interface IHubFooterDTO extends ICommonHubSettingsDTO {
    footerText: string | null;
    showWebsiteFooterLogoImage: boolean;
}

export type THubConfigurationDTO =
    | IHubRegistrationDTO
    | IHubSignInDTO
    | IHubBrandingDTO
    | IHubFooterDTO;

export type IHubConfigurationSettingsDTO = THubConfigurationDTO[];

// ── Hub Organizations ────────────────────────────────────────────────────────

export interface ICreateHubOrganizationDTO {
    name: string;
    type?: string;
    ticker?: string;
    externalOrganizationId?: string;
}

export interface IHubOrganizationDTO {
    hubOrganizationId: number;
    name: string;
    type?: string;
    ticker?: string;
    externalOrganizationId?: string;
}

// ── Hub Attendees ────────────────────────────────────────────────────────────

export interface ICreateHubAttendeeDTO {
    organizationId: number;
    firstName: string;
    lastName: string;
    email: string;
    assistantEmail?: string;
    title?: string;
    salutation?: string;
    phoneNumber?: string;
    accessCode?: string;
    registrationStatus: string;
    externalAttendeeId?: string;
}

export interface IHubAttendeeDTO extends ICreateHubAttendeeDTO {
    hubAttendeeId: number;
}

export interface IHubAttendeeListItemDTO {
    attendeeId: number;
    organizationId?: number;
    firstName: string;
    lastName: string;
    email: string;
    organizationName: string;
    organizationType: string;
    title: string;
    phoneNumber: string;
    salutation: string;
    assistantEmail: string;
    accessCode?: string;
    externalAttendeeId?: string;
    registrationDate: string;
    registrationStatus: string;
    customFieldValues?: { [customFieldId: number]: string };
}

export type THubAttendeesSortKeys =
    | 'lastName'
    | 'email'
    | 'organization.name'
    | 'organization.organizationType'
    | 'title'
    | 'phoneNumber'
    | 'salutation'
    | 'assistantEmail'
    | 'accessCode'
    | 'externalAttendeeId'
    | 'registrationDate'
    | 'registrationStatus';

export interface IHubAttendeesListDTO {
    content: IHubAttendeeListItemDTO[];
    totalElements: number;
}

export interface IGetHubAttendeesRequestParams extends IPaginationParams {
    accountId: number;
    eventHubId: number;
    sortField?: THubAttendeesSortKeys;
    sortOrder?: SortOrder;
}

// ── Hub Modules ──────────────────────────────────────────────────────────────

export enum HubModuleType {
    EVENTS = 'EVENTS',
    SPEAKERS = 'SPEAKERS',
    RICH_TEXT = 'RICH_TEXT',
    MY_EVENTS = 'MY_EVENTS',
}

export enum HubModuleLayout {
    CAROUSEL = 'CAROUSEL',
    LIST = 'LIST',
}

export enum HubModuleDefaultView {
    COLLAPSED = 'COLLAPSED',
    EXPANDED = 'EXPANDED',
}

export interface ICreateHubModuleCommonDTO {
    enabled: boolean;
    label: string;
    displayLabel: boolean;
    positionOrder: number;
}

export interface IEventsBaseModuleDTO extends ICreateHubModuleCommonDTO {
    description: string;
    layout: HubModuleLayout;
    displaySpeakers: boolean;
    defaultView?: HubModuleDefaultView;
    displayUpcomingEvents: boolean;
    upcomingEventDays?: number;
    displayPastEvents: boolean;
    pastEventDays?: number;
}

export interface ICreateEventsHubModuleDTO extends IEventsBaseModuleDTO {
    moduleType: HubModuleType.EVENTS;
    attributes?: IEventAttributeValue[];
}

export interface IEventsHubModuleDTO extends ICreateEventsHubModuleDTO {
    moduleId: number;
}

export interface ISpeakerReferenceDTO extends IAccountContactDTO {
    positionOrder: number;
}

export interface ICreateSpeakersHubModuleDTO extends ICreateHubModuleCommonDTO {
    moduleType: HubModuleType.SPEAKERS;
    description: string;
    layout: HubModuleLayout.LIST;
    displayName: boolean;
    displayTitle: boolean;
    displayOrg: boolean;
    displayProfileImage: boolean;
    displayBio: boolean;
    speakers: ISpeakerReferenceDTO[];
}

export interface ISpeakersHubModuleDTO extends ICreateSpeakersHubModuleDTO {
    moduleId: number;
}

export interface ICreateRichTextHubModuleDTO extends ICreateHubModuleCommonDTO {
    moduleType: HubModuleType.RICH_TEXT;
    content: string;
}

export interface IRichTextHubModuleDTO extends ICreateRichTextHubModuleDTO {
    moduleId: number;
}

export interface ICreateMyEventsHubModuleDTO extends IEventsBaseModuleDTO {
    moduleType: HubModuleType.MY_EVENTS;
}

export interface IMyEventsHubModuleDTO extends ICreateMyEventsHubModuleDTO {
    moduleId: number;
}

export type TCreateHubModuleDTO = ICreateEventsHubModuleDTO | ICreateSpeakersHubModuleDTO | ICreateRichTextHubModuleDTO | ICreateMyEventsHubModuleDTO;
export type THubModuleDTO = IEventsHubModuleDTO | ISpeakersHubModuleDTO | IRichTextHubModuleDTO | IMyEventsHubModuleDTO;

export interface IRemovedHubModuleDTO {
    moduleId: number;
}

export interface IHubModulesBatchDTO {
    addedModules: TCreateHubModuleDTO[];
    updatedModules: THubModuleDTO[];
    removedModules: IRemovedHubModuleDTO[];
}

// ── Hub Pages ────────────────────────────────────────────────────────────────

export enum EventHubPageType {
    CUSTOM = 'CUSTOM',
    LINK = 'LINK',
}

export interface IHubPageDTO {
    hubPageId: number;
    pageName: string;
    displayName: string;
    pagePath: string;
    description: string;
    enabled: boolean;
    pageType: EventHubPageType;
}

export interface IHubPageDetailsDTO extends IHubPageDTO {
    modules: THubModuleDTO[];
}

export interface ICustomHubPageDTO extends IHubPageDTO {
    pageType: EventHubPageType.CUSTOM;
}

export type THubPageDTO = ICustomHubPageDTO;

export interface ICreateHubPageDTO extends Partial<IHubModulesBatchDTO> {
    pageName: string;
    displayName: string;
    pagePath: string;
    description: string;
    enabled: boolean;
    pageType: EventHubPageType;
}

export interface IUpdateHubPageDTO extends IHubPageDTO, Partial<IHubModulesBatchDTO> {}

export interface ICreateHubMenuLinkDTO {
    label: string;
    externalLink: string;
    enabled?: boolean;
}

export interface IHubMenuLinkDTO extends ICreateHubMenuLinkDTO {
    hubMenuLinkId: number;
    enabled: boolean;
    positionOrder: number;
}

export const isHubMenuLink = (item: IHubPageDTO | IHubMenuLinkDTO): item is IHubMenuLinkDTO => {
    return 'hubMenuLinkId' in item;
};

export const isCustomPage = (page: IHubPageDTO | IHubMenuLinkDTO): page is ICustomHubPageDTO => {
    return 'pageType' in page && page.pageType === EventHubPageType.CUSTOM;
};
